import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getUsers, createUser, updateUser, deleteUser, 
  downloadEmployeeTemplate, bulkImportUsers,
  impersonateUser, adminResetPassword
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Search, Plus, Upload, Download, MoreVertical, Edit, Trash2, Filter, LogIn, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const DEPARTMENTS = ['Marketing', 'Human Resources Management', 'Travel', 'Design & Development', 'Accounts (Credit & Collection)', 'Operations', 'Sales', 'Sales Coordinators'];
const ENTITIES = ['UOSPL', 'UAPL', 'UAFPL', 'United Group'];

export const EmployeesPage = () => {
  const { user, isSuperAdmin, isAdmin, isCoordinator } = useAuth();
  const canManage = isSuperAdmin || isAdmin || isCoordinator;
  const ROLES = isSuperAdmin ? ['Super Admin', 'Admin', 'Coordinator', 'Member'] : ['Admin', 'Coordinator', 'Member'];
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [resetPwdOpen, setResetPwdOpen] = useState(false);
  const [resetPwdTarget, setResetPwdTarget] = useState(null);
  const [resetPwdValue, setResetPwdValue] = useState('');
  const [resetPwdLoading, setResetPwdLoading] = useState(false);
  const [impersonateConfirmOpen, setImpersonateConfirmOpen] = useState(false);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  
  const defaultDept = isSuperAdmin ? '' : user?.department || '';
  
  const [customDepts, setCustomDepts] = useState([]);
  const [newDept, setNewDept] = useState('');
  const [formData, setFormData] = useState({
    emp_code: '', full_name: '', email: '', manager_name: '', manager_email: '',
    manager_name_2: '', manager_email_2: '',
    department: defaultDept, departments: defaultDept ? [defaultDept] : [], role: 'Member', entity: '', employee_category: '', status: 'Active', password: ''
  });

  useEffect(() => { fetchEmployees(); }, [statusFilter, departmentFilter]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (departmentFilter) params.department = departmentFilter;
      const response = await getUsers(params);
      setEmployees(response.data);
    } catch (error) { toast.error('Failed to fetch employees'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({
      emp_code: '', full_name: '', email: '', manager_name: '', manager_email: '',
      manager_name_2: '', manager_email_2: '',
      department: defaultDept, departments: defaultDept ? [defaultDept] : [], role: 'Member', entity: '', employee_category: '', status: 'Active', password: ''
    });
    setEditingEmployee(null);
  };

  const openEditDialog = (emp) => {
    setEditingEmployee(emp);
    const empDepts = (emp.departments && emp.departments.length) ? emp.departments : (emp.department ? [emp.department] : []);
    setFormData({
      emp_code: emp.emp_code || '', full_name: emp.full_name, email: emp.email,
      manager_name: emp.manager_name || '', manager_email: emp.manager_email || '',
      manager_name_2: emp.manager_name_2 || '', manager_email_2: emp.manager_email_2 || '',
      department: emp.department, departments: empDepts, role: emp.role, entity: emp.entity || '',
      employee_category: emp.employee_category || '',
      status: emp.status, password: ''
    });
    setFormOpen(true);
  };

  const isAdminRole = formData.role === 'Admin';

  const toggleDepartment = (dept) => {
    setFormData((prev) => {
      const set = new Set(prev.departments || []);
      if (set.has(dept)) set.delete(dept); else set.add(dept);
      const arr = Array.from(set);
      return { ...prev, departments: arr, department: arr[0] || '' };
    });
  };

  const addNewDepartment = () => {
    const d = newDept.trim();
    if (!d) { toast.error('Enter a department name'); return; }
    if (!DEPARTMENTS.includes(d) && !customDepts.includes(d)) setCustomDepts((prev) => [...prev, d]);
    if (isAdminRole) {
      setFormData((prev) => {
        const set = new Set(prev.departments || []); set.add(d);
        const arr = Array.from(set);
        return { ...prev, departments: arr, department: arr[0] || d };
      });
    } else {
      setFormData((prev) => ({ ...prev, department: d, departments: [d] }));
    }
    setNewDept('');
    toast.success(`Added "${d}"`);
  };

  const deptPool = Array.from(new Set([
    ...DEPARTMENTS,
    ...employees.map((e) => e.department).filter(Boolean),
    ...employees.flatMap((e) => e.departments || []),
    ...customDepts,
  ])).filter(Boolean);

  const handleRoleChange = (v) => {
    setFormData((prev) => {
      if (v === 'Admin') {
        const arr = (prev.departments && prev.departments.length) ? prev.departments : (prev.department ? [prev.department] : []);
        return { ...prev, role: v, departments: arr };
      }
      // Non-admin → collapse to single department
      return { ...prev, role: v, departments: prev.department ? [prev.department] : [] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name || !formData.email || !formData.role) {
      toast.error('Please fill all required fields'); return;
    }
    // Department(s) validation
    if (isSuperAdmin && isAdminRole) {
      if (!formData.departments || formData.departments.length === 0) {
        toast.error('Select at least one department for the Admin'); return;
      }
    } else if (!formData.department) {
      toast.error('Please select a department'); return;
    }
    if (!formData.emp_code) { toast.error('Employee Code is required'); return; }
    if (!formData.manager_name) { toast.error('Manager Name is required'); return; }
    if (!formData.manager_email) { toast.error('Manager Email is required'); return; }
    if (!formData.email.endsWith('@united-group.in')) {
      toast.error('Email must be from @united-group.in domain'); return;
    }
    // Build a normalized payload: Admins may have multiple departments; everyone else single.
    const depts = (isAdminRole && formData.departments && formData.departments.length)
      ? formData.departments
      : (formData.department ? [formData.department] : []);
    const payload = { ...formData, departments: depts, department: depts[0] || formData.department };
    try {
      if (editingEmployee) {
        const updateData = { ...payload };
        if (!updateData.password) delete updateData.password;
        await updateUser(editingEmployee.id, updateData);
        toast.success('Employee updated');
      } else {
        if (!formData.password) { toast.error('Password is required'); return; }
        await createUser(payload);
        toast.success('Employee created');
      }
      setFormOpen(false); resetForm(); fetchEmployees();
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const handleDelete = async () => {
    if (!employeeToDelete) return;
    try {
      await deleteUser(employeeToDelete.id);
      toast.success('Employee deleted');
      setDeleteDialogOpen(false); setEmployeeToDelete(null); fetchEmployees();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadEmployeeTemplate();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'employee_template.xlsx'; a.click();
      toast.success('Template downloaded');
    } catch (error) { toast.error('Failed to download template'); }
  };

  const handleBulkImport = async () => {
    if (!importFile) { toast.error('Please select a file'); return; }
    setImportLoading(true);
    try {
      const response = await bulkImportUsers(importFile);
      const { success_count, error_count } = response.data;
      if (success_count > 0) toast.success(`Imported ${success_count} employees`);
      if (error_count > 0) toast.error(`${error_count} rows failed`);
      setImportDialogOpen(false); setImportFile(null); fetchEmployees();
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
    finally { setImportLoading(false); }
  };

  const handleImpersonate = async () => {
    if (!impersonateTarget) return;
    try {
      const res = await impersonateUser(impersonateTarget.id);
      const newToken = res.data?.access_token;
      if (!newToken) { toast.error('Failed to impersonate'); return; }
      // Preserve original Super Admin token for later revert
      const currentToken = localStorage.getItem('token');
      if (currentToken && !localStorage.getItem('originalToken')) {
        localStorage.setItem('originalToken', currentToken);
        localStorage.setItem('originalUserName', user?.full_name || 'Super Admin');
      }
      localStorage.setItem('token', newToken);
      toast.success(`Logged in as ${impersonateTarget.full_name}`);
      setImpersonateConfirmOpen(false);
      setImpersonateTarget(null);
      // Hard reload to refresh auth context and route guards
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwdTarget) return;
    if (!resetPwdValue || resetPwdValue.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetPwdLoading(true);
    try {
      await adminResetPassword(resetPwdTarget.id, resetPwdValue);
      toast.success(`Password reset for ${resetPwdTarget.full_name}`);
      setResetPwdOpen(false);
      setResetPwdTarget(null);
      setResetPwdValue('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally { setResetPwdLoading(false); }
  };

  const getRoleColor = (role) => {
    const map = {
      'Super Admin': 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/20',
      'Admin': 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20',
      'Coordinator': 'bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/20',
    };
    return map[role] || 'bg-gray-500/15 text-gray-600 dark:text-gray-400 border border-gray-500/20';
  };

  if (!canManage) {
    return <div className="text-center py-12"><p className="text-muted-foreground">You don't have permission to access this page</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Employees</h1>
          <p className="text-muted-foreground mt-1">Manage team members</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate} data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-1.5" /> Template
          </Button>
          {(isSuperAdmin || isAdmin) && (
            <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)} data-testid="bulk-import-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Import
            </Button>
          )}
          <Button onClick={() => { resetForm(); setFormOpen(true); }} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="add-employee-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-border/50">
        <CardContent className="p-4">
          <form onSubmit={(e) => { e.preventDefault(); fetchEmployees(); }} className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name, email, emp code..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-employees-input" />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-32"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              {isSuperAdmin && (
                <Select value={departmentFilter || "all"} onValueChange={(v) => setDepartmentFilter(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-40"><SelectValue placeholder="All Departments" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button type="submit" variant="secondary"><Filter className="h-4 w-4 mr-1.5" /> Apply</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card className="border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div></div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12"><p className="text-muted-foreground">No employees found</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Employee</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Emp Code</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Department</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Role</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Entity</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Manager</th>
                    <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="text-right p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.id} className="border-b border-border hover:bg-accent/50 transition-colors" data-testid={`employee-row-${emp.id}`}>
                      <td className="p-4">
                        <p className="font-medium">{emp.full_name}</p>
                        <p className="text-xs text-muted-foreground">{emp.email}</p>
                      </td>
                      <td className="p-4 text-sm font-mono">{emp.emp_code || '-'}</td>
                      <td className="p-4 text-sm">{(emp.departments && emp.departments.length) ? emp.departments.join(', ') : emp.department}</td>
                      <td className="p-4"><Badge className={getRoleColor(emp.role)}>{emp.role}</Badge></td>
                      <td className="p-4 text-sm">{emp.entity || '-'}</td>
                      <td className="p-4 text-sm">{emp.manager_name || '-'}</td>
                      <td className="p-4"><Badge className={emp.status === 'Active' ? 'bg-emerald-500/15 text-emerald-600 border border-emerald-500/20' : 'bg-gray-500/15 text-gray-600 border border-gray-500/20'}>{emp.status}</Badge></td>
                      <td className="p-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(emp)} data-testid={`edit-emp-${emp.id}`}><Edit className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                            {isSuperAdmin && emp.id !== user?.id && (
                              <>
                                <DropdownMenuItem onClick={() => { setImpersonateTarget(emp); setImpersonateConfirmOpen(true); }} data-testid={`impersonate-${emp.id}`}>
                                  <LogIn className="h-4 w-4 mr-2" /> Login As
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setResetPwdTarget(emp); setResetPwdValue(''); setResetPwdOpen(true); }} data-testid={`reset-pwd-${emp.id}`}>
                                  <KeyRound className="h-4 w-4 mr-2" /> Reset Password
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuItem onClick={() => { setEmployeeToDelete(emp); setDeleteDialogOpen(true); }} className="text-destructive focus:text-destructive" data-testid={`delete-emp-${emp.id}`}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={formOpen} onOpenChange={(open) => { setFormOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            <DialogDescription>{editingEmployee ? 'Update employee details' : 'Fill in the details to add a new team member'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee Code *</Label>
                <Input value={formData.emp_code} onChange={(e) => setFormData({ ...formData, emp_code: e.target.value })} placeholder="EMP001" required data-testid="emp-code-input" />
              </div>
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="John Doe" required data-testid="full-name-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john.doe@united-group.in" required disabled={!!editingEmployee} data-testid="email-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department *</Label>
                {isSuperAdmin ? (
                  isAdminRole ? (
                    <div className="rounded-md border border-input p-3 space-y-2 max-h-44 overflow-y-auto" data-testid="department-multiselect">
                      <p className="text-xs text-muted-foreground">Select one or more departments this Admin can manage</p>
                      {deptPool.map(d => (
                        <label key={d} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={(formData.departments || []).includes(d)}
                            onCheckedChange={() => toggleDepartment(d)}
                            data-testid={`department-option-${d.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                          />
                          <span>{d}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v, departments: [v] })}>
                      <SelectTrigger data-testid="department-select"><SelectValue placeholder="Select department" /></SelectTrigger>
                      <SelectContent>{deptPool.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  )
                ) : (
                  <Input value={(formData.departments && formData.departments.length ? formData.departments : [formData.department]).join(', ')} disabled className="bg-muted" data-testid="department-display" />
                )}
                {isSuperAdmin && (
                  <div className="flex gap-2 pt-1">
                    <Input
                      value={newDept}
                      onChange={(e) => setNewDept(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewDepartment(); } }}
                      placeholder="Add a new department…"
                      className="h-8 text-sm"
                      data-testid="new-department-input"
                    />
                    <Button type="button" variant="outline" size="sm" onClick={addNewDepartment} data-testid="add-department-btn">Add</Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={handleRoleChange}>
                  <SelectTrigger data-testid="role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Manager Name *</Label>
                <Input value={formData.manager_name} onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })} placeholder="Jane Smith" required data-testid="manager-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Manager Email *</Label>
                <Input type="email" value={formData.manager_email} onChange={(e) => setFormData({ ...formData, manager_email: e.target.value })} placeholder="jane@united-group.in" required data-testid="manager-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Second Manager Name <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input value={formData.manager_name_2} onChange={(e) => setFormData({ ...formData, manager_name_2: e.target.value })} placeholder="Optional — second reporting manager" data-testid="manager-name-2-input" />
              </div>
              <div className="space-y-2">
                <Label>Second Manager Email <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input type="email" value={formData.manager_email_2} onChange={(e) => setFormData({ ...formData, manager_email_2: e.target.value })} placeholder="second.mgr@united-group.in" data-testid="manager-email-2-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entity</Label>
                <Select value={formData.entity || "none"} onValueChange={(v) => setFormData({ ...formData, entity: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="entity-select"><SelectValue placeholder="Select entity" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {ENTITIES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Employee Category <span className="text-xs text-muted-foreground">(used for module access control)</span></Label>
              <Input value={formData.employee_category} onChange={(e) => setFormData({ ...formData, employee_category: e.target.value })} placeholder="e.g., Plant Operations, Legal, Sales" data-testid="employee-category-input" />
            </div>
            {!editingEmployee && (
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Enter password" required data-testid="password-input" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setFormOpen(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="save-employee-btn">
                {editingEmployee ? 'Update' : 'Add Employee'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Import Employees</DialogTitle>
            <DialogDescription>Upload an Excel file. Download the template first.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button variant="outline" onClick={handleDownloadTemplate} className="w-full"><Download className="h-4 w-4 mr-2" /> Download Template</Button>
            <div className="space-y-2">
              <Label>Upload File</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkImport} disabled={!importFile || importLoading} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="confirm-import-btn">
              {importLoading ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete "{employeeToDelete?.full_name}"? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-emp-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Impersonate Confirmation (Super Admin only) */}
      <AlertDialog open={impersonateConfirmOpen} onOpenChange={setImpersonateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Login as {impersonateTarget?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be logged in as <strong>{impersonateTarget?.email}</strong> ({impersonateTarget?.role} · {impersonateTarget?.department}).
              An "Exit Impersonation" banner will appear so you can return to your Super Admin session at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleImpersonate} className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold" data-testid="confirm-impersonate-btn">Login As</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Dialog (Super Admin only) */}
      <Dialog open={resetPwdOpen} onOpenChange={setResetPwdOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {resetPwdTarget?.full_name} ({resetPwdTarget?.email}). They will need to use this password to log in.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input
              type="password"
              value={resetPwdValue}
              onChange={(e) => setResetPwdValue(e.target.value)}
              placeholder="At least 6 characters"
              autoFocus
              data-testid="reset-pwd-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwdOpen(false)}>Cancel</Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPwdLoading || !resetPwdValue || resetPwdValue.length < 6}
              className="bg-[#F5A623] hover:bg-[#D48C1B] text-black font-semibold"
              data-testid="confirm-reset-pwd-btn"
            >
              {resetPwdLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
